import { z } from 'zod';
// ─── Judge ───────────────────────────────────────────────────────────────────
//
// A Judge is a reusable, org-scoped evaluator with an explicit input schema
// (JSON Schema) and a *restricted* output schema: a single object whose
// properties may only be boolean | number | enum(string[]).
//
// Judges are versioned: every edit cuts a new immutable JudgeVersion.
export const JudgeTypeSchema = z.enum(['CODE', 'LLM_ONE_SHOT']);
// ─── Restricted output schema ────────────────────────────────────────────────
export const JudgeOutputBooleanPropSchema = z.object({
    type: z.literal('boolean'),
    description: z.string().optional(),
});
export const JudgeOutputNumberPropSchema = z.object({
    type: z.literal('number'),
    description: z.string().optional(),
});
export const JudgeOutputEnumPropSchema = z.object({
    type: z.literal('enum'),
    description: z.string().optional(),
    values: z.array(z.string().min(1)).min(1),
});
export const JudgeOutputPropertySchema = z.discriminatedUnion('type', [
    JudgeOutputBooleanPropSchema,
    JudgeOutputNumberPropSchema,
    JudgeOutputEnumPropSchema,
]);
const propertyNameRe = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
export const JudgeOutputSchemaSchema = z
    .object({
    type: z.literal('object'),
    properties: z.record(z.string(), JudgeOutputPropertySchema),
})
    .refine((s) => Object.keys(s.properties).length > 0, {
    message: 'outputSchema must declare at least one property',
})
    .refine((s) => Object.keys(s.properties).every((k) => propertyNameRe.test(k)), {
    message: 'outputSchema property names must match /^[a-zA-Z_][a-zA-Z0-9_]*$/',
});
// Input schema is a free-form JSON Schema. We don't validate its full shape
// here — Ajv validates at service layer when the user POSTs. We require it
// to at least be an object so the editor has something coherent to render.
export const JudgeInputSchemaSchema = z.object({ type: z.literal('object') }).passthrough();
// ─── Version ─────────────────────────────────────────────────────────────────
export const JudgeVersionSchema = z.object({
    id: z.string(),
    judgeId: z.string(),
    versionNumber: z.number().int().positive(),
    judgeType: JudgeTypeSchema,
    inputSchema: JudgeInputSchemaSchema,
    outputSchema: JudgeOutputSchemaSchema,
    code: z.string().nullable(),
    modelConfigId: z.string().nullable(),
    systemPrompt: z.string().nullable(),
    promptTemplate: z.string().nullable(),
    createdAt: z.date(),
    createdBy: z.string().nullable(),
});
export const JudgeSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    judgeType: JudgeTypeSchema,
    currentVersionId: z.string().nullable(),
    currentVersion: JudgeVersionSchema.nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
// ─── Create / Update inputs ──────────────────────────────────────────────────
const baseJudgePayload = {
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    inputSchema: JudgeInputSchemaSchema,
    outputSchema: JudgeOutputSchemaSchema,
};
/**
 * Flat schema with cross-field refinement. We don't use `discriminatedUnion`
 * here because `nestjs-zod`'s `createZodDto` requires a `ZodObject` base.
 */
export const CreateJudgeInputSchema = z
    .object({
    ...baseJudgePayload,
    judgeType: JudgeTypeSchema,
    code: z.string().min(1).optional(),
    modelConfigId: z.string().min(1).optional(),
    systemPrompt: z.string().min(1).optional(),
    /** LLM_ONE_SHOT only. Handlebars template; receives `{ input }`. */
    promptTemplate: z.string().optional(),
})
    .superRefine((val, ctx) => {
    if (val.judgeType === 'CODE') {
        if (!val.code) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CODE judge requires `code`',
                path: ['code'],
            });
        }
        if (val.modelConfigId || val.systemPrompt || val.promptTemplate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CODE judge cannot set modelConfigId / systemPrompt / promptTemplate',
                path: ['judgeType'],
            });
        }
    }
    else {
        if (!val.modelConfigId || !val.systemPrompt) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'LLM_ONE_SHOT judge requires modelConfigId + systemPrompt',
                path: ['systemPrompt'],
            });
        }
        if (val.code) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'LLM_ONE_SHOT judge cannot set `code`',
                path: ['code'],
            });
        }
    }
});
// Update: same shape as create (sans top-level type — type is fixed for a Judge).
// Cuts a new version with the provided fields.
const baseUpdate = {
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    inputSchema: JudgeInputSchemaSchema.optional(),
    outputSchema: JudgeOutputSchemaSchema.optional(),
    code: z.string().min(1).optional(),
    modelConfigId: z.string().min(1).optional(),
    systemPrompt: z.string().min(1).optional(),
    promptTemplate: z.string().nullable().optional(),
};
export const UpdateJudgeInputSchema = z.object(baseUpdate);
// ─── Run ─────────────────────────────────────────────────────────────────────
export const RunJudgeInputSchema = z.object({
    input: z.unknown(),
    /** Pin to a specific version. Omit to use current. */
    versionNumber: z.number().int().positive().optional(),
});
export const JudgeOutputValueSchema = z.union([z.boolean(), z.number(), z.string()]);
/**
 * A single score cell: the value (constrained by the declared property
 * type) paired with a free-form `reason` string the judge fills in to
 * justify the score. Every score the runner emits is shaped like this.
 */
export const JudgeScoreCellSchema = z.object({
    value: JudgeOutputValueSchema,
    reason: z.string(),
});
export const RunJudgeResultSchema = z.object({
    judgeVersionId: z.string(),
    versionNumber: z.number().int().positive(),
    output: z.record(z.string(), JudgeScoreCellSchema),
    durationMs: z.number().int().nonnegative(),
});
export const JudgeRunSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    judgeId: z.string(),
    judgeVersionId: z.string(),
    input: z.unknown(),
    output: z.record(z.string(), JudgeScoreCellSchema).nullable(),
    error: z.string().nullable(),
    durationMs: z.number().int().nullable(),
    createdAt: z.date(),
});
//# sourceMappingURL=judge.js.map